import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Tab, Tabs, Divider } from '@mui/material';
import PageHeader from '../components/common/PageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusBadge from '../components/common/StatusBadge';
import StudentDetail from '../components/students/StudentDetail';
import DocumentCategoryGrid from '../components/documents/DocumentCategoryGrid';
import ContactRemarks from '../components/students/ContactRemarks';
import PreviousLoans from '../components/students/PreviousLoans';
import CibilReportsSection from '../components/students/CibilReportsSection';
import ApplicationFormView from '../components/students/ApplicationFormView';
import { getStudentById } from '../services/studentService';

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ py: 2 }}>
    {value === index && children}
  </Box>
);

const ConsultancyStudentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [applicationForm, setApplicationForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchStudent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getStudentById(id);
      setStudent(response.data?.data?.student || null);
      setApplicationForm(response.data?.data?.applicationForm || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load student');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchStudent();
  }, [id, fetchStudent]);

  if (loading) return <LoadingSpinner fullPage />;

  if (error || !student) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader title="Student Not Found" />
        <Typography color="text.secondary">{error || 'The requested student was not found.'}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={student.name || 'Student'}
        breadcrumbs={[
          { label: 'Consultancy Portal', link: '/consultancy-portal' },
          { label: student.fileNumber || student.name },
        ]}
      >
        <StatusBadge status={student.status} />
      </PageHeader>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Overview" />
          <Tab label="Documents" />
          <Tab label="Remarks" />
          <Tab label="Loan History" />
          <Tab label="CIBIL Reports" />
          <Tab label="Loan Form" />
        </Tabs>

        <Box sx={{ px: 3 }}>
          {/* Overview */}
          <TabPanel value={activeTab} index={0}>
            <StudentDetail student={student} onRefresh={fetchStudent} />
          </TabPanel>

          {/* Documents */}
          <TabPanel value={activeTab} index={1}>
            <DocumentCategoryGrid studentId={id} />
          </TabPanel>

          {/* Remarks */}
          <TabPanel value={activeTab} index={2}>
            <ContactRemarks studentId={id} />
          </TabPanel>

          {/* Loan History */}
          <TabPanel value={activeTab} index={3}>
            <PreviousLoans studentId={id} />
          </TabPanel>

          {/* CIBIL Reports */}
          <TabPanel value={activeTab} index={4}>
            <CibilReportsSection studentId={id} student={student} onRefresh={fetchStudent} />
          </TabPanel>

          {/* Loan Form */}
          <TabPanel value={activeTab} index={5}>
            <ApplicationFormView applicationForm={applicationForm} student={student} onRefresh={fetchStudent} />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default ConsultancyStudentDetailPage;
